import type {CodeownersError, SplitCodeownersError} from '@github-ui/code-view-types'
import type {Repository} from '@github-ui/current-repository'
import {codeownersErrorPath} from '@github-ui/paths'
import type {RefInfo} from '@github-ui/repos-types'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {BugIcon, DotFillIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {Flash, IconButton} from '@primer/react'
import {Dialog, Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {useRef, useState} from 'react'

import styles from './CodeownerFileBanner.module.css'

export const CodeOwnerValidationState = {
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  VALIDATED: 'VALIDATED',
} as const

export type CodeOwnerValidationState = (typeof CodeOwnerValidationState)[keyof typeof CodeOwnerValidationState]

export function CodeownerFileBanner({
  errors,
  state,
}: {
  errors: SplitCodeownersError[]
  state: CodeOwnerValidationState
}) {
  const [open, setOpen] = useState(false)
  const expandErrorButton = useRef<HTMLButtonElement>(null)

  if (state === CodeOwnerValidationState.ERROR) {
    return (
      <Flash variant="warning" className={styles.Flash}>
        Failed to validate this CODEOWNERS file
      </Flash>
    )
  }

  if (state === CodeOwnerValidationState.LOADING) {
    return (
      <Flash variant="default" className={styles.Flash}>
        Validating CODEOWNERS rules...
      </Flash>
    )
  }

  if (errors.length === 0) {
    return (
      <Flash variant="success" className={styles.Flash}>
        This CODEOWNERS file is valid.
      </Flash>
    )
  }

  return (
    <>
      <Flash variant="warning" className={styles.Flash_1}>
        <Octicon icon={BugIcon} />
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClick={() => setOpen(!open)} className={styles.Box}>
          This CODEOWNERS file contains errors
          <IconButton
            aria-label="View CODEOWNERS errors"
            tooltipDirection="s"
            ref={expandErrorButton}
            size="small"
            icon={KebabHorizontalIcon}
            className={styles.IconButton}
          />
        </div>
      </Flash>
      <Dialog
        isOpen={open}
        onDismiss={() => setOpen(false)}
        returnFocusRef={expandErrorButton}
        className={styles.Dialog}
      >
        <Dialog.Header>CODEOWNERS errors</Dialog.Header>
        <div className={styles.Box_1}>
          {errors.map((error, index) => {
            // eslint-disable-next-line @eslint-react/no-array-index-key
            return <CodeownersErrorDetails key={index} error={error} />
          })}
        </div>
      </Dialog>
    </>
  )
}

function CodeownersErrorDetails({error}: {error: SplitCodeownersError}) {
  return (
    <div className={styles.Box_2}>
      {`${error.kind} on line ${error.line}${error.suggestion ? `: ${error.suggestion}` : ''}`}
      <pre className={clsx(styles.Flash, styles.ErrorCodeBlock)}>
        <code>
          {error.linePrefix}
          <b className={styles.Box_3}>{error.lineError}</b>
          {error.lineSuffix}
        </code>
      </pre>
    </div>
  )
}

const MAX_ERROR_OFFSET = 30
const TRUNCATED_LINE_PREFIX = '…'

export function splitCodeownersError(error: CodeownersError): SplitCodeownersError {
  let line = error.source.trim()
  let errorOffset = error.column - 1
  let endOffset = calculateEndOffset(errorOffset, error.end_column, line)

  if (errorOffset > MAX_ERROR_OFFSET) {
    const trimmedCharacters = errorOffset - MAX_ERROR_OFFSET
    line = TRUNCATED_LINE_PREFIX + line.slice(trimmedCharacters)
    errorOffset -= trimmedCharacters - TRUNCATED_LINE_PREFIX.length
    endOffset -= trimmedCharacters - TRUNCATED_LINE_PREFIX.length
  }

  return {
    ...error,
    linePrefix: line.substring(0, errorOffset),
    lineError: line.substring(errorOffset, endOffset),
    lineSuffix: line.substring(endOffset),
  }
}

function calculateEndOffset(errorOffset: number, endColumn: number | null, line: string) {
  if (endColumn) {
    return endColumn
  } else if (line.substring(errorOffset).indexOf(' ') > 0) {
    return line.indexOf(' ', errorOffset)
  } else {
    return line.length
  }
}

export function CodeownersErrorLineIndicator() {
  return <Octicon icon={DotFillIcon} aria-label="This line contains CODEOWNERS errors" className={styles.Octicon} />
}

export function fetchCodeownersValidity(repo: Repository, refInfo: RefInfo, path: string) {
  return verifiedFetchJSON(
    codeownersErrorPath({owner: repo.ownerLogin, repo: repo.name, commitish: refInfo.name, filePath: path}),
    {method: 'GET'},
  )
}
